import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TrendingUp, ArrowRight, FileText, GraduationCap, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader } from "@/components/Section";
import { api, resolveImg } from "@/lib/api";

export default function Results() {
  const [progress, setProgress] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [active, setActive] = useState(null);
  const [docView, setDocView] = useState(null);

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/achievements").then((r) => setAchievements(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="results-page">
      <Seo title="Results & Achievements" description="Genuine SSC / 10th student progress at Prasad Tutor Group — before and after board results shared by PTG students." />
      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <SectionHeader center={false} light eyebrow="Results & Achievements" title="Student Progress"
            subtitle="Student progress and results shown from genuine academic records shared by our students." />
        </Container>
      </section>

      <section className="py-16 sm:py-24">
        <Container>
          <div className="flex items-center gap-3 mb-10">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-blue-900"><TrendingUp className="h-6 w-6" /></div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="ssc-progress-heading">SSC / 10th Student Progress</h2>
          </div>

          {progress.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {progress.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl transition-all overflow-hidden" data-testid={`progress-card-${i}`}>
                    <div className="bg-gradient-to-br from-blue-900 to-sky-700 px-6 py-5 text-white flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15"><GraduationCap className="h-6 w-6" /></div>
                      <div>
                        <h3 className="font-bold text-lg leading-tight" data-testid={`progress-name-${i}`}>{p.student_name}</h3>
                        <p className="text-sky-200 text-xs font-semibold uppercase tracking-wider">{p.mode === "grade" ? "Academic Progress" : "Student Progress"}</p>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8">
                      {p.mode === "grade" ? (
                        <>
                          <div className="flex items-center justify-center gap-4 sm:gap-6">
                            <div className="text-center">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Before</p>
                              <p className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold text-slate-500" data-testid={`progress-before-${i}`}>{p.before_label}</p>
                            </div>
                            <ArrowRight className="h-8 w-8 text-amber-500 shrink-0" />
                            <div className="text-center">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">After</p>
                              <p className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-blue-900" data-testid={`progress-after-${i}`}>{p.after_label}</p>
                            </div>
                          </div>
                          {p.highlight && (
                            <div className="mt-6 rounded-xl bg-sky-50 border border-sky-100 py-3 px-4 text-center" data-testid={`progress-highlight-${i}`}>
                              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Verified subject improvement</p>
                              <p className="mt-1 font-bold text-slate-700">{p.highlight}: <span className="text-slate-500">{p.highlight_before}</span> <ArrowRight className="inline h-4 w-4 text-slate-400" /> <span className="text-blue-900">{p.highlight_after}</span></p>
                            </div>
                          )}
                          <p className="mt-4 text-center text-xs text-slate-400">Grade-based report — open “View Results” for the full grade sheets.</p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-center gap-4 sm:gap-6">
                            <div className="text-center">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{p.before_label}</p>
                              <p className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-slate-500" data-testid={`progress-before-${i}`}>{p.before_percentage}</p>
                            </div>
                            <ArrowRight className="h-8 w-8 text-amber-500 shrink-0" />
                            <div className="text-center">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">{p.after_label}</p>
                              <p className="mt-1 font-heading text-4xl sm:text-5xl font-extrabold text-blue-900" data-testid={`progress-after-${i}`}>{p.after_percentage}</p>
                            </div>
                          </div>
                          <div className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-slate-50 border border-slate-100 py-2.5 text-sm font-semibold text-slate-600">
                            <span>{p.before_label}</span><ArrowRight className="h-4 w-4 text-slate-400" /><span>{p.after_label}</span>
                          </div>
                        </>
                      )}
                      <Button onClick={() => setActive(p)} className="mt-6 w-full bg-blue-900 hover:bg-blue-950 h-11" data-testid={`progress-view-${i}`}>
                        <FileText className="mr-2 h-4 w-4" /> View Results
                      </Button>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-slate-500" data-testid="progress-empty">Student progress will appear here once added from the admin panel.</p>
          )}

          <p className="mt-10 text-xs text-slate-400 max-w-2xl">Percentages and documents are shown exactly as issued by the respective school / board, and are shared with the students' consent.</p>
        </Container>
      </section>

      {achievements.length > 0 && (
        <section className="pb-16 sm:pb-24" data-testid="std9-section">
          <Container>
            <div className="flex items-center gap-3 mb-10">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-blue-900"><Award className="h-6 w-6" /></div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="std9-heading">Standard 9 Results &amp; Achievements</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {achievements.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.06}>
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all overflow-hidden h-full flex flex-col" data-testid={`ach-card-${i}`}>
                    <div className="bg-gradient-to-br from-blue-900 to-sky-700 px-5 py-4 text-white">
                      <h3 className="font-bold leading-tight" data-testid={`ach-name-${i}`}>{a.student_name}</h3>
                      <p className="text-sky-200 text-[11px] font-semibold uppercase tracking-wider">{a.label || "Standard 9"}</p>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      {a.percentage ? (
                        <div className="text-center">
                          <p className="font-heading text-4xl font-extrabold text-blue-900" data-testid={`ach-pct-${i}`}>{a.percentage}</p>
                          {a.school && <p className="mt-1 text-xs text-slate-500 leading-snug">{a.school}</p>}
                        </div>
                      ) : null}
                      {a.subjects && a.subjects.length > 0 && (
                        <div className="mt-3 space-y-1.5" data-testid={`ach-subjects-${i}`}>
                          {a.subjects.map((s, si) => {
                            const ci = s.lastIndexOf(":");
                            const nm = ci > -1 ? s.slice(0, ci).trim() : s;
                            const mk = ci > -1 ? s.slice(ci + 1).trim() : "";
                            return (
                              <div key={si} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                                <span className="text-slate-600">{nm}</span><span className="font-semibold text-slate-800">{mk}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {a.note && <p className="mt-3 text-[11px] text-slate-400 leading-snug">{a.note}</p>}
                      {a.doc ? (
                        <Button onClick={() => setDocView({ url: resolveImg(a.doc), title: `${a.student_name} — ${a.label || "Standard 9 Result"}` })} className="mt-4 w-full bg-blue-900 hover:bg-blue-950 h-10" data-testid={`ach-view-${i}`}>
                          <FileText className="mr-2 h-4 w-4" /> View Result
                        </Button>
                      ) : <div className="mt-4" />}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Single result-document viewer (Nidhi / Sneha) */}
      <Dialog open={!!docView} onOpenChange={(o) => !o && setDocView(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ach-doc-modal">
          {docView && (
            <div>
              <DialogTitle className="font-heading text-lg font-extrabold text-slate-900">{docView.title}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Genuine school result document.</DialogDescription>
              <a href={docView.url} target="_blank" rel="noreferrer" className="block mt-4" data-testid="ach-doc-link">
                <img src={docView.url} alt={docView.title} className="w-full object-contain max-h-[72vh] bg-white rounded-lg border" />
              </a>
              <p className="text-xs text-slate-400 mt-3">Tap the document to open it full size in a new tab.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="progress-docs-modal">
          {active && (
            <div>
              <DialogTitle className="font-heading text-xl font-extrabold text-slate-900">{active.student_name} — Result Documents</DialogTitle>
              <DialogDescription className="text-sm text-slate-500 mt-0.5">{active.mode === "grade"
                ? `${active.before_label} → ${active.after_label} • Academic Progress`
                : `${active.before_label} (${active.before_percentage}) → ${active.after_label} (${active.after_percentage})`}</DialogDescription>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                {[["Before", active.before_label, active.before_percentage, active.before_doc],
                  ["After", active.after_label, active.after_percentage, active.after_doc]].map(([tag, label, pct, doc]) => (
                  <div key={tag} className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{tag}: {label}</span>
                      <span className="text-sm font-extrabold text-blue-900">{pct}</span>
                    </div>
                    {doc ? (
                      <a href={resolveImg(doc)} target="_blank" rel="noreferrer" data-testid={`progress-doc-${tag.toLowerCase()}`}>
                        <img src={resolveImg(doc)} alt={`${active.student_name} ${label} result document`} className="w-full object-contain max-h-[60vh] bg-white" />
                      </a>
                    ) : <div className="p-8 text-center text-slate-400 text-sm">Document not available</div>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">Tap a document to open it full size in a new tab.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
